import { DEFAULT_BASEMAP_ID, type MapEngine } from '@mighty-map/map-engine';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { appApi } from '../../api/appApi';
import { brandAssets, documentTitle, brandText } from '../../lib/brand';
import { toolsMinimised } from '../../lib/chrome';
import { orderedThemes } from '../../lib/config';
import { engineCamera, useBimViewCamera } from '../../lib/viewCamera';
import { getDataToken } from '../../state/dataToken';
import { selectActiveTheme, useMapStore } from '../../state/mapStore';
import { useViewMode } from '../../state/viewMode';
import { applyAccent } from '../../theme/accent';
import { startLiveUpdates } from '../layers/liveUpdates';
import { runtimeAware } from '../layers/runtimeLayers';
import { useBim } from '../bim/bimStore';
import { ThemeCarousel } from '../themes/ThemeCarousel';
import { MapToolsController } from '../tools/MapToolsController';
import { CONTROLLER_TOOLS } from '../tools/registry';
import { ToolPanel } from '../tools/ToolPanel';
import { Header } from './Header';
import { IdentifyPopup } from './IdentifyPopup';
import { BIM_ENGINE_TOOLS, toolShownIn } from './bimChrome';
import './bimChrome.css';
import { IntroSplash } from './IntroSplash';
import { MapControls } from './MapControls';
import { useToolEngine } from '../bim/bimToolEngine';
import { MapView } from './MapView';
import { ModeSwitch } from './ModeSwitch';
import { ScaleBar } from './ScaleBar';
import { useShell } from './shellStore';
import { StatusLine } from './StatusLine';
import { restoreZoomRanges } from '../layers/layerEdits';

const WidgetHostOverlay = lazy(() => import('../widget-host/WidgetHostOverlay'));

const TOOLS_KEY = 'mm-tools-min';

function readToolsKey(): string | null {
  try {
    return sessionStorage.getItem(TOOLS_KEY);
  } catch {
    return null;
  }
}

/** The tools column's minimised state: minimised by default on phones, remembered for the session once toggled. */
function useToolsMinimised(): [boolean, () => void] {
  const [stored, setStored] = useState(readToolsKey);
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = (): void => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const minimised = toolsMinimised(stored, width);
  const toggle = (): void => {
    const next = minimised ? '0' : '1';
    try {
      sessionStorage.setItem(TOOLS_KEY, next);
    } catch {
      /* not remembered */
    }
    setStored(next);
  };
  return [minimised, toggle];
}

export function Shell() {
  const config = useMapStore((s) => s.config);
  const themeId = useMapStore((s) => s.themeId);
  const theme = useMapStore(selectActiveTheme);
  const setTheme = useMapStore((s) => s.setTheme);
  const openTool = useShell((s) => s.openTool);
  const panelSeen = useShell((s) => s.panelSeen);
  const toggleTool = useShell((s) => s.toggleTool);
  const view = useViewMode((s) => s.mode);
  const closeTool = useShell((s) => s.closeTool);
  const [toolsMin, toggleToolsMin] = useToolsMinimised();
  const [error, setError] = useState<string | null>(null);
  const [engine, setEngine] = useState<MapEngine | null>(null);
  const engineRef = useRef<MapEngine | null>(null);
  // Every tool gets the engine through Layers Pro's runtime-layer visibility: an overlay a tool draws that the
  // person switched off in Layers Pro stays off however often the tool redraws it.
  const onEngine = useCallback((e: MapEngine | null) => {
    const aware = e ? runtimeAware(e) : null;
    engineRef.current = aware;
    setEngine(aware);
  }, []);

  useEffect(() => {
    let cancelled = false;
    appApi
      .getConfig()
      .then((c) => {
        if (!cancelled) {
          useMapStore.getState().init(c, DEFAULT_BASEMAP_ID);
          restoreZoomRanges();
        }
      })
      .catch((e: unknown) => setError(`Could not load the project: ${e instanceof Error ? e.message : String(e)}`));
    return () => {
      cancelled = true;
    };
  }, []);

  const dataApiUrl = config?.project.data_api_url;
  const channel = config?.project.channel;
  useEffect(() => {
    if (!dataApiUrl || !channel) return;
    return startLiveUpdates({
      dataApiUrl,
      channel,
      getToken: getDataToken,
      getEngine: () => engineRef.current,
      onError: () => setError('Data service unavailable'),
    });
  }, [dataApiUrl, channel]);

  // The project accent drives every highlight in the app (theme/accent.ts).
  const brand = config?.project.brand;
  useEffect(() => {
    if (brand !== undefined) applyAccent(brand);
  }, [brand]);

  const projectTitle = config?.project.title;
  useEffect(() => {
    if (projectTitle) document.title = documentTitle(projectTitle, theme?.title);
  }, [projectTitle, theme?.title]);

  const themes = useMemo(() => (config ? orderedThemes(config) : []), [config]);
  // The left map-tools column drives the view on screen: the engine in Map and Globe, the model camera in BIM.
  const mapCamera = useMemo(() => (engine ? engineCamera(engine) : null), [engine]);
  const bimCamera = useBimViewCamera((s) => s.camera);
  const tool = CONTROLLER_TOOLS.find((t) => t.id === openTool) ?? null;
  // A map-only tool left open on entering BIM stays mounted under its hidden pane, and is back on leaving.
  // Data Passport stays in BIM where the project's model takes added data (its BIM settings name a grid).
  const bimData = useBim((s) => s.grid != null);
  const toolShown = !!tool && toolShownIn(tool.id, view === 'bim', bimData);
  // Snapshot and Print Pro work on the model in BIM: while one is open there, it gets the BIM tool engine.
  const bimTool = !!tool && BIM_ENGINE_TOOLS.has(tool.id);
  const toolEngine = useToolEngine(engine, bimTool);
  const widgetEngine = useToolEngine(engine);

  if (!config) {
    return (
      <div className="mm-app">
        <div className="splash" role="status">
          {error ?? 'Loading…'}
        </div>
      </div>
    );
  }

  const { logoUrl } = brandAssets(config.project.brand);
  return (
    <div
      className="mm-app"
      data-view={view}
      data-bim-data={bimData ? '' : undefined}
      data-panel-open={toolShown ? '' : undefined}
      data-panel-seen={panelSeen ? '' : undefined}
      data-tools-min={toolsMin ? '' : undefined}
    >
      <main className="mm-stage">
        <MapView onEngine={onEngine} />
        <IdentifyPopup />
      </main>
      <div className="mm-widget-host">
        <Suspense fallback={null}>
          <WidgetHostOverlay engine={widgetEngine} />
        </Suspense>
      </div>
      <Header project={config.project} />
      <MapToolsController openTool={toolShown ? openTool : null} onToggle={toggleTool} />
      {tool && (
        <ToolPanel
          key={tool.id}
          tool={tool}
          engine={bimTool ? toolEngine : engine}
          logoUrl={logoUrl}
          onClose={closeTool}
        />
      )}
      <MapControls
        camera={view === 'bim' ? bimCamera : mapCamera}
        history={view === 'bim' ? 'bim' : 'map'}
        home={theme?.camera ?? null}
        homeView={view === 'globe' ? 'globe' : 'map'}
        globeHome={theme?.camera?.globe}
        minimised={toolsMin}
        onToggleMinimised={toggleToolsMin}
      />
      <ScaleBar engine={engine} />
      <ModeSwitch />
      <ThemeCarousel themes={themes} activeId={themeId} onSelect={setTheme} />
      <StatusLine error={error} />
      <IntroSplash
        logoUrl={logoUrl}
        title={config.project.title}
        subtitle={brandText(config.project.brand, 'tagline') ?? config.project.subtitle ?? null}
      />
    </div>
  );
}
